/**
 * "Why This Scope?" panel — PRP-003.
 *
 * Renders the rule-firing trace for a single scope generation. Default
 * collapsed; expands to show rules grouped by rule_type, with per-row
 * drill-down to conditions matched and actions emitted.
 */

import * as React from 'react';
import { ChevronDown, ChevronRight, ExternalLink, ListChecks } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '../../lib/utils';
import type { RuleTraceEntry, RuleTraceResponse } from '../../types/trace';
import type { RuleType } from '../../types/rules';
import { RULE_TYPE_LABELS, SeverityBadge, RuleTypeBadge } from './RuleBadges';
import { humanize } from '../../lib/plainEnglish';

// ---------------------------------------------------------------------------
// Group + ordering rules
// ---------------------------------------------------------------------------

const TYPE_ORDER: RuleType[] = [
  'trigger',
  'activity_resolution',
  'companion',
  'quantity_heuristic',
  'validation',
];

function groupByType(traces: RuleTraceEntry[]): Record<RuleType, RuleTraceEntry[]> {
  const out: Record<RuleType, RuleTraceEntry[]> = {
    trigger: [],
    activity_resolution: [],
    companion: [],
    quantity_heuristic: [],
    validation: [],
  };
  // chronological within each group
  const sorted = [...traces].sort((a, b) => a.fired_at.localeCompare(b.fired_at));
  for (const t of sorted) {
    const key = (t.rule_type || 'trigger') as RuleType;
    if (!out[key]) out[key] = [];
    out[key].push(t);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KeyValueTable({ data }: { data: Record<string, unknown> }): React.ReactElement {
  const keys = Object.keys(data || {});
  if (keys.length === 0) {
    return <p className="text-xs italic text-slate-500">no conditions captured</p>;
  }
  return (
    <table className="w-full text-xs">
      <tbody>
        {keys.map((k) => (
          <tr key={k} className="border-b border-slate-100 last:border-0">
            <td className="py-1 pr-3 font-medium text-slate-600 align-top whitespace-nowrap">
              {humanize(k)}
            </td>
            <td className="py-1 text-slate-800 break-words font-mono text-[11px]">
              {renderValue(data[k])}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function renderValue(v: unknown): React.ReactNode {
  if (v === null || v === undefined) return <span className="text-slate-400">—</span>;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  return <pre className="whitespace-pre-wrap">{JSON.stringify(v, null, 2)}</pre>;
}

function ActionList({ actions }: { actions: Array<Record<string, unknown>> }): React.ReactElement {
  if (!actions || actions.length === 0) {
    return <p className="text-xs italic text-slate-500">no actions</p>;
  }
  return (
    <ul className="space-y-1.5 text-xs">
      {actions.map((a, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="mt-0.5 inline-block h-1.5 w-1.5 flex-none rounded-full bg-emerald-500" />
          <div className="font-mono text-[11px] text-slate-700">
            <span className="font-semibold text-slate-900">{String(a.verb || a.type || '?')}</span>
            {Object.entries(a)
              .filter(([k]) => k !== 'verb' && k !== 'type')
              .map(([k, v]) => (
                <span key={k} className="ml-2 text-slate-600">
                  {humanize(k)}=<span className="text-slate-800">{stringifyVal(v)}</span>
                </span>
              ))}
          </div>
        </li>
      ))}
    </ul>
  );
}

function stringifyVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function PhaseChip({ phase }: { phase: string }): React.ReactElement {
  const label =
    phase === 'global'
      ? 'Pre-build'
      : phase === 'post_build'
        ? 'Post-build'
        : phase === 'per_item'
          ? 'Per-item'
          : phase === 'validator'
            ? 'Validator'
            : phase === 'retrieval_context'
              ? 'Retrieval'
              : phase;
  return (
    <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Row + group
// ---------------------------------------------------------------------------

function TraceRow({ entry }: { entry: RuleTraceEntry }): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const retired = entry.current_rule_status && entry.current_rule_status !== 'active';

  // Reason: PRP-002 rule library uses ?q= search; deep-linking by name
  // lets users land on the right rule even before deep-link-by-id ships.
  const ruleLibraryHref = `/admin/rules?q=${encodeURIComponent(entry.rule_name)}`;

  return (
    <li className="rounded-md border border-slate-200 bg-white">
      <div className="flex items-start gap-3 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          className="mt-0.5 flex-none text-slate-400 hover:text-slate-700"
          aria-label={open ? 'Hide details' : 'Show details'}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <SeverityBadge severity={entry.rule_severity} />
            <RuleTypeBadge ruleType={entry.rule_type} />
            <PhaseChip phase={entry.evaluation_phase} />
            <Link
              to={ruleLibraryHref}
              className={cn(
                'inline-flex items-center gap-1 truncate text-sm font-medium text-slate-900 hover:text-emerald-700',
                retired && 'line-through opacity-70',
              )}
              title={retired ? `Rule is currently ${entry.current_rule_status}` : entry.rule_name}
            >
              {entry.rule_name}
              <ExternalLink className="h-3 w-3 flex-none text-slate-400" />
            </Link>
          </div>
          {entry.rationale_text ? (
            <p className="mt-1 text-xs text-slate-600">{entry.rationale_text}</p>
          ) : null}
          {entry.target_item_id ? (
            <p className="mt-0.5 text-[11px] font-mono text-slate-500">
              on {entry.target_item_id}
            </p>
          ) : null}
        </div>
      </div>
      {open ? (
        <div className="border-t border-slate-100 bg-slate-50 px-3 py-3 space-y-3">
          <section>
            <h5 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Conditions matched
            </h5>
            <KeyValueTable data={entry.conditions_matched} />
          </section>
          <section>
            <h5 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Actions emitted
            </h5>
            <ActionList actions={entry.actions_emitted} />
          </section>
        </div>
      ) : null}
    </li>
  );
}

function TraceGroup({
  ruleType,
  entries,
}: {
  ruleType: RuleType;
  entries: RuleTraceEntry[];
}): React.ReactElement {
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {RULE_TYPE_LABELS[ruleType]} ({entries.length})
      </h4>
      <ul className="space-y-1.5">
        {entries.map((e) => (
          <TraceRow key={e.id} entry={e} />
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export interface EstimateRuleTracePanelProps {
  trace: RuleTraceResponse | null;
  loading?: boolean;
  error?: string | null;
  /** Start expanded if true. Defaults to collapsed (PRP §7). */
  defaultOpen?: boolean;
}

export default function EstimateRuleTracePanel({
  trace,
  loading = false,
  error = null,
  defaultOpen = false,
}: EstimateRuleTracePanelProps): React.ReactElement {
  const [open, setOpen] = React.useState(defaultOpen);

  const count = trace?.trace_count ?? 0;

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-emerald-600" />
          <span className="font-semibold text-slate-900">Why This Scope?</span>
          {loading ? (
            <span className="text-xs text-slate-500">loading…</span>
          ) : trace ? (
            <span className="text-xs text-slate-500">{count} rules fired</span>
          ) : null}
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>
      {open ? (
        <div className="border-t border-slate-100 px-4 py-4 space-y-4">
          {error ? (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          ) : null}

          {loading && !trace ? (
            <p className="text-sm text-slate-500">Loading trace…</p>
          ) : null}

          {trace && trace.pre_phase3 ? (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              This estimate was generated before rule tracing was enabled. Re-generate the
              scope to see trace data.
            </p>
          ) : null}

          {trace && !trace.pre_phase3 && trace.trace_count === 0 ? (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              No rules fired on this estimate. This is unusual — please notify support.
            </p>
          ) : null}

          {trace && trace.trace_count > 0 ? (
            <div className="space-y-5">
              {TYPE_ORDER.map((rt) => {
                const grouped = groupByType(trace.traces);
                const entries = grouped[rt];
                if (!entries || entries.length === 0) return null;
                return <TraceGroup key={rt} ruleType={rt} entries={entries} />;
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
